import config from 'config';
import { push } from 'connected-react-router';
import { actions as sessionActions } from '@mulesoft/exchange-ui-portals-store/lib/domains/session';
import { actions as analyticsActions } from '~/domains/analytics';
import { selectors as commonSelectors } from '~/domains/common';
import { selectors as featureFlagsSelectors } from '~/domains/featureFlags';
import { ANG_DEFAULT_ENABLED } from '~/domains/featureFlags/keys';
import { actions as modalActions } from '~/domains/modal';
import * as searchService from '~/services/searchService';
import * as platformSearchService from '~/services/platformSearchService';
import { getBelongingOrganizationIds } from '~/utils/organization';
import { EVENT_TYPES } from '~/analytics/events';
import ANGOptOutModal from '~/components/Modals/ANGOptOutModal';
import ANGOnboardingModal from '~/components/Modals/ANGOnboardingModal';
import * as actions from './actionTypes';
import * as selectors from './selectors';

export function changeResults(results) {
  return {
    type: actions.CHANGE_RESULTS,
    ...results
  };
}

export function setFacets(facets) {
  return {
    type: actions.SET_FACETS,
    facets
  };
}

export function getRootOrganizationsFromFacets(facetsOrganizations, profile) {
  const organizationIds = facetsOrganizations?.map(({ id }) => id) ?? [];
  const belongingOrganizationIds = getBelongingOrganizationIds(profile);
  const rootOrganizations = organizationIds.filter((id) =>
    belongingOrganizationIds.includes(id)
  );

  return rootOrganizations;
}

export function setRootOrganizations(rootOrganizations) {
  return {
    type: actions.SET_ROOT_ORGANIZATIONS,
    rootOrganizations
  };
}

export function handleFacetsError(error) {
  return {
    type: actions.FETCHING_FACETS_ERROR,
    error
  };
}

export function updateFacets(facets) {
  return {
    type: actions.UPDATE_FACETS,
    facets
  };
}

export function getRootOrganizations() {
  return async (dispatch, getState) => {
    const state = getState();
    const profile = commonSelectors.profile(state);
    const context = commonSelectors.context(state);
    const isPlatformSearch = selectors.isPlatformSearchEnabled(state);

    if (!profile) {
      return null;
    }

    let facets;
    const rootOrganizations = selectors.rootOrganizations(state);

    if (rootOrganizations) {
      return rootOrganizations;
    }

    try {
      if (isPlatformSearch) {
        facets = await platformSearchService.getFacets({
          context,
          query: {}
        });
      } else {
        facets = await searchService.getFacets({
          context,
          query: {}
        });
      }
    } catch (error) {
      if (error.status === 401) {
        throw error;
      }

      dispatch(handleFacetsError(error));

      return null;
    }

    const rootOrganizationsFromFacets = getRootOrganizationsFromFacets(
      facets.organizations,
      profile
    );

    dispatch(setRootOrganizations(rootOrganizationsFromFacets));

    return rootOrganizationsFromFacets;
  };
}

export function enableANGSearch() {
  return async (dispatch, getState) => {
    const state = getState();
    const isANGDefaultFlagEnabled = featureFlagsSelectors.featureFlag(
      state,
      ANG_DEFAULT_ENABLED
    );

    if (isANGDefaultFlagEnabled) {
      await dispatch(
        sessionActions.setUserProperty({
          namespace: 'exchange',
          key: 'isANGSearchDisabled',
          value: 'false'
        })
      );
    } else {
      await dispatch(
        sessionActions.setUserProperty({
          namespace: 'exchange',
          key: 'isANGSearchEnabled',
          value: 'true'
        })
      );
    }

    const homeURL = `${config.externalURI.pathname}/?new_search=`;

    dispatch(push(homeURL)); // hack trick to re-launch home resolver so facets are being loaded
  };
}

export function disableANGSearch() {
  return async (dispatch, getState) => {
    const state = getState();
    const isANGDefaultFlagEnabled = featureFlagsSelectors.featureFlag(
      state,
      ANG_DEFAULT_ENABLED
    );

    if (isANGDefaultFlagEnabled) {
      await dispatch(
        sessionActions.setUserProperty({
          namespace: 'exchange',
          key: 'isANGSearchDisabled',
          value: 'true'
        })
      );
    } else {
      await dispatch(
        sessionActions.setUserProperty({
          namespace: 'exchange',
          key: 'isANGSearchEnabled',
          value: 'false'
        })
      );
    }

    const homeURL = `${config.externalURI.pathname}/`;

    dispatch(push(homeURL));
  };
}

export function optOutOfANGSearch() {
  return async (dispatch, getState) => {
    const state = getState();
    const isANGDefaultFlagEnabled = featureFlagsSelectors.featureFlag(
      state,
      ANG_DEFAULT_ENABLED
    );

    if (isANGDefaultFlagEnabled) {
      await dispatch(
        analyticsActions.trackEvent(EVENT_TYPES.ANG_SEARCH_OPT_OUT)
      );

      await dispatch(disableANGSearch());
    } else {
      dispatch(modalActions.openModal({ modalType: ANGOptOutModal.modalType }));
    }
  };
}

export function optInToANGSearch() {
  return async (dispatch, getState) => {
    const state = getState();
    const isANGDefaultFlagEnabled = featureFlagsSelectors.featureFlag(
      state,
      ANG_DEFAULT_ENABLED
    );

    if (isANGDefaultFlagEnabled) {
      await dispatch(
        analyticsActions.trackEvent(EVENT_TYPES.ANG_SEARCH_OPT_IN)
      );

      await dispatch(enableANGSearch());
    } else {
      dispatch(
        modalActions.openModal({ modalType: ANGOnboardingModal.modalType })
      );
    }
  };
}

export function platformSearchGetMinorVersion({
  groupId,
  assetId,
  minorVersion
}) {
  return async (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);

    dispatch({ type: actions.PLATFORM_MINOR_VERSION_REQUEST });

    try {
      const data = await platformSearchService.getMinorVersionMetadata({
        context,
        groupId,
        assetId,
        minorVersion
      });

      dispatch({
        type: actions.PLATFORM_MINOR_VERSION_SUCCESS,
        data
      });
    } catch (error) {
      if (error.status === 401) {
        throw error;
      }

      dispatch({
        type: actions.PLATFORM_MINOR_VERSION_FAILURE,
        error
      });
    }
  };
}
